import { startTransition, useState } from 'react';
import useSWR from 'swr';

import { fetchJobs, type Job } from '#/lib/jobs';
import type { SanityCareersFeed } from '#/lib/sanity/queries/sections';
import { cn } from '#/lib/utils';
import { Headline } from '#/components/Headline';
import { Link } from '#/components/Link';
import { Listbox } from '#/components/Listbox';

const ALL = 'All Departments';

type Props = SanityCareersFeed & {
  initialJobs?: Job[] | null;
};

export function CareersFeed(props: Props) {
  const { data } = useSWR('careersFeed', () => fetchJobs(), {
    fallbackData: props.initialJobs ?? undefined,
    keepPreviousData: true,
    onErrorRetry: () => {
      // Don't retry on error, use fallback data instead
    },
  });

  // Use initialData as fallback if fetch fails or returns null/undefined
  const jobs = data ?? props.initialJobs ?? null;

  const departmentLabels = Array.from(
    new Set(jobs?.map((career) => career.department.label) || []),
  );
  const departments = [ALL, ...departmentLabels];
  // Filter is useless with a single listing or a single department.
  const showDepartmentFilter =
    (jobs?.length ?? 0) > 1 && departmentLabels.length > 1;

  const [department, setDepartment] = useState(departments[0]);

  const addCount = (department: string) => {
    return `${department} (
                ${
                  department === ALL
                    ? (jobs?.length ?? 0)
                    : (jobs?.filter((d) => d.department.label === department)
                        .length ?? 0)
                }
                )`;
  };

  const options = departments.map((department) => ({
    label: addCount(department),
    value: department,
  }));

  const longestLabel = options.reduce((longest, option) => {
    return option.label.length > longest.length ? option.label : longest;
  }, '');

  const filteredJobs =
    jobs?.filter(
      (c) => c.department.label === department || department === ALL,
    ) ?? [];

  return (
    <div className="p-main transition-all" id="board">
      <div className="mx-auto max-w-[980px]">
        <Headline value={props.title} variant={5} className="mb-20 m:mb-25" />
        {showDepartmentFilter ? (
          <label className="block m:inline-block">
            {/* Set longest label to be the width of the select to avoid layout shift */}
            <span
              className="w-full opacity-0 pr-40 block h-0 pointer-events-none"
              aria-hidden
            >
              {longestLabel}
            </span>
            <Listbox
              name="Department"
              displayValue={addCount(department)}
              defaultValue={department}
              value={department}
              className="m:w-auto"
              options={options}
              onChange={(val) => {
                // Keep list filtering off the Listbox interaction critical path.
                startTransition(() => setDepartment(val));
              }}
            />
          </label>
        ) : null}
        <ul
          className={cn(
            '-mx-8 flex flex-wrap gap-y-15 m:gap-y-16',
            showDepartmentFilter ? 'mt-25 m:mt-50' : 'mt-0',
          )}
        >
          {filteredJobs.map((career) => {
            return (
              <li
                key={career.uuid}
                className="block w-full px-8 m:w-1/2 l:w-1/3"
              >
                <Link
                  className="group relative block h-full rounded-8 outline-none focus-visible:ring-1 focus-visible:ring-cipher"
                  href={career.url}
                  target="_blank"
                >
                  <div className="relative h-full min-h-130 rounded-8 bg-grayscale-01 p-30 transition-colors group-hover:bg-grayscale-02">
                    <h3 className="mb-15 text-16 font-[440] leading-120 -tracking-1">
                      {career.name}
                    </h3>
                    <p className="text-14 font-light text-grayscale-06">
                      {career.workLocation.label}
                    </p>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
